import React, { useState, useEffect } from 'react';
import { api } from '../../api';
import './Admin.css';

export default function AdminCategories() {
  const [list, setList] = useState([]);
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ labelText: '', uri: '', descriptionText: '', imageSrc: '', application: [] });
  const [uploading, setUploading] = useState(false);

  const applicationList = Array.isArray(form.application) ? form.application : (form.application ? String(form.application).split(',').map((s) => s.trim()).filter(Boolean) : []);

  const load = () => {
    setLoading(true);
    api.get('/api/categories.php')
      .then((data) => { setList(Array.isArray(data) ? data : []); setMessage({ type: '', text: '' }); })
      .catch((err) => setMessage({ type: 'error', text: err.error || 'Failed to load' }))
      .finally(() => setLoading(false));
  };

  const loadApplications = () => {
    api.get('/api/applications.php')
      .then((data) => setApplications(Array.isArray(data) ? data : []))
      .catch(() => setApplications([]));
  };

  useEffect(() => { load(); loadApplications(); }, []);

  const openAdd = () => {
    setEditing(null);
    setAdding(true);
    setForm({ labelText: '', uri: '', descriptionText: '', imageSrc: '', application: [] });
  };

  const parseApplication = (v) => {
    if (Array.isArray(v)) return v.filter(Boolean);
    if (!v) return [];
    return String(v).split(',').map((s) => s.trim()).filter(Boolean);
  };

  const openEdit = (row) => {
    setAdding(false);
    setEditing(row.uri);
    setForm({
      labelText: row.labelText || '',
      uri: row.uri || '',
      descriptionText: row.descriptionText || '',
      imageSrc: row.imageSrc || '',
      application: parseApplication(row.application),
    });
  };

  const toggleApplication = (appName) => {
    setForm((f) => {
      const list = Array.isArray(f.application) ? f.application : parseApplication(f.application);
      const has = list.includes(appName);
      return { ...f, application: has ? list.filter((a) => a !== appName) : [...list, appName] };
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'Please choose an image file (JPEG, PNG, GIF, WebP).' });
      return;
    }
    setUploading(true);
    setMessage({ type: '', text: '' });
    const formData = new FormData();
    formData.append('file', file);
    api.upload('/api/upload.php', formData)
      .then((data) => {
        setForm((f) => ({ ...f, imageSrc: data.url || data.path || '' }));
        setMessage({ type: 'success', text: 'Image uploaded.' });
      })
      .catch((err) => setMessage({ type: 'error', text: err.error || 'Upload failed' }))
      .finally(() => { setUploading(false); e.target.value = ''; });
  };

  const save = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    const names = Array.isArray(form.application) ? form.application : parseApplication(form.application);
    const applicationIds = names
      .map((name) => applications.find((a) => a.name === name)?.id)
      .filter((id) => id != null);
    const payload = {
      ...form,
      application: applicationIds,
    };
    try {
      if (editing) {
        await api.put(`/api/category.php?uri=${encodeURIComponent(editing)}`, payload);
        setMessage({ type: 'success', text: 'Category updated.' });
      } else {
        await api.post('/api/categories.php', payload);
        setMessage({ type: 'success', text: 'Category created.' });
      }
      setEditing(null);
      setAdding(false);
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.error || 'Save failed' });
    }
  };

  const remove = async (uri) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await api.delete(`/api/category.php?uri=${encodeURIComponent(uri)}`);
      setMessage({ type: 'success', text: 'Category deleted.' });
      load();
    } catch (err) {
      setMessage({ type: 'error', text: err.error || 'Delete failed' });
    }
  };

  const imagePreviewUrl = (() => {
    if (!form.imageSrc) return null;
    if (form.imageSrc.startsWith('http') || form.imageSrc.startsWith('data:')) return form.imageSrc;
    if (form.imageSrc.startsWith('public://')) return 'https://admin.tablemate.net/sites/default/files/' + form.imageSrc.replace('public://', '');
    const base = (process.env.REACT_APP_API_URL || 'https://api.tablemate.net').replace(/\/$/, '');
    return base + (form.imageSrc.startsWith('/') ? form.imageSrc : '/' + form.imageSrc);
  })();

  const resolveImageUrl = (imageSrc) => {
    if (!imageSrc) return null;
    if (imageSrc.startsWith('http') || imageSrc.startsWith('data:')) return imageSrc;
    if (imageSrc.startsWith('public://')) return 'https://admin.tablemate.net/sites/default/files/' + imageSrc.replace('public://', '');
    const base = (process.env.REACT_APP_API_URL || 'https://api.tablemate.net').replace(/\/$/, '');
    return base + (imageSrc.startsWith('/') ? imageSrc : '/' + imageSrc);
  };

  return (
    <div className="admin-section">
      <h1>Categories</h1>
      {message.text && <div className={`admin-message ${message.type}`}>{message.text}</div>}
      <div className="admin-toolbar">
        <button type="button" className="admin-btn admin-btn-primary" onClick={openAdd}>Add category</button>
      </div>
      {(editing !== null || adding) && (
        <div className="admin-form">
          <h2>{editing ? 'Edit category' : 'New category'}</h2>
          <form onSubmit={save}>
            <label>Label text *</label>
            <input value={form.labelText} onChange={(e) => setForm({ ...form, labelText: e.target.value })} required />
            <label>URI *</label>
            <input value={form.uri} onChange={(e) => setForm({ ...form, uri: e.target.value })} required disabled={!!editing} />
            <label>Description</label>
            <textarea value={form.descriptionText} onChange={(e) => setForm({ ...form, descriptionText: e.target.value })} />
            <label>Category icon (image)</label>
            <div className="admin-image-upload">
              {imagePreviewUrl && (
                <div className="admin-image-preview">
                  <img src={imagePreviewUrl} alt="Category icon" onError={(e) => { e.target.style.display = 'none'; }} />
                </div>
              )}
              <div className="admin-image-actions">
                <label className="admin-btn admin-btn-secondary" style={{ marginBottom: 0, cursor: 'pointer' }}>
                  {uploading ? 'Uploading…' : (form.imageSrc ? 'Change image' : 'Choose image')}
                  <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleImageChange} disabled={uploading} style={{ display: 'none' }} />
                </label>
                {form.imageSrc && (
                  <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => setForm({ ...form, imageSrc: '' })}>Clear</button>
                )}
              </div>
            </div>
            <label>Applications</label>
            <div className="admin-checkbox-group">
              {applications.map((app) => (
                <label key={app.id} className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    checked={applicationList.includes(app.name)}
                    onChange={() => toggleApplication(app.name)}
                  />
                  <span>{app.name}</span>
                </label>
              ))}
            </div>
            {applications.length === 0 && <p className="admin-manage-empty">No applications. Add some under Applications.</p>}
            <div className="admin-form-actions">
              <button type="submit" className="admin-btn admin-btn-primary">Save</button>
              <button type="button" className="admin-btn admin-btn-secondary" onClick={() => { setEditing(null); setAdding(false); setForm({ labelText: '', uri: '', descriptionText: '', imageSrc: '', application: [] }); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}
      <div className="admin-table-wrap">
        {loading ? <p style={{ padding: '1rem' }}>Loading…</p> : (
          <table className="admin-table">
            <thead>
              <tr>
                <th style={{ width: 56 }}>Icon</th>
                <th>Label</th>
                <th>URI</th>
                <th>Applications</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {list.map((row) => {
                const iconUrl = resolveImageUrl(row.imageSrc);
                return (
                <tr key={row.uri}>
                  <td>
                    {iconUrl ? (
                      <img src={iconUrl} alt="" className="admin-category-list-icon" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="admin-category-list-icon-placeholder">—</span>
                    )}
                  </td>
                  <td>{row.labelText}</td>
                  <td>{row.uri}</td>
                  <td>{(row.application || '').toString().slice(0, 80)}{(row.application || '').toString().length > 80 ? '…' : ''}</td>
                  <td>
                    <button type="button" className="admin-btn admin-btn-secondary admin-btn-small" onClick={() => openEdit(row)}>Edit</button>
                    <button type="button" className="admin-btn admin-btn-danger admin-btn-small" onClick={() => remove(row.uri)}>Delete</button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
